import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

interface AdminUser {
  readonly userId: string;
  readonly email: string;
  readonly role: 'admin';
}

// Shape returned by api-gateway POST /auth/admin-login
interface GatewayTokenResponse {
  readonly access_token: string;
  readonly token_type: string;
  readonly expires_in: number;
}

// Shape returned by api-gateway GET /auth/me (admin token)
interface GatewayAdminMeResponse {
  readonly user_id: string;
  readonly email: string;
  readonly role: 'admin';
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);

  private readonly TOKEN_KEY = 'ss_admin_token';

  private _user = signal<AdminUser | null>(null);
  private _token = signal<string | null>(sessionStorage.getItem(this.TOKEN_KEY));

  readonly user = this._user.asReadonly();
  readonly isAuthenticated = computed(() => this._token() !== null);

  login(email: string, password: string): Promise<void> {
    return this.http
      .post<GatewayTokenResponse>(`${environment.apiUrl}/auth/admin-login`, { email, password })
      .toPromise()
      .then((tokenResponse) => {
        if (tokenResponse === undefined) return;
        sessionStorage.setItem(this.TOKEN_KEY, tokenResponse.access_token);
        this._token.set(tokenResponse.access_token);

        return this.http
          .get<GatewayAdminMeResponse>(`${environment.apiUrl}/auth/me`, {
            headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
          })
          .toPromise()
          .then((me) => {
            if (me === undefined) return;
            this._user.set({
              userId: me.user_id,
              email: me.email,
              role: 'admin',
            });
            void this.router.navigate(['/dashboard']);
          });
      });
  }

  logout(): void {
    sessionStorage.removeItem(this.TOKEN_KEY);
    this._token.set(null);
    this._user.set(null);
    void this.router.navigate(['/login']);
  }

  getToken(): string | null {
    return this._token();
  }
}
